import { useMemo } from 'react';
import { QueryResult, useQuery, WatchQueryFetchPolicy } from '@apollo/client';
import { Session_DetailQuery as SessionDetailQuery, Webcast } from '@graphql/gql/graphql';
import useLocaleForQueries from '@hooks/useLocaleForQueries';
import useIsLive from '@hooks/useIsLive';
import useSessionStatus from '@tools/sessionUtils/useSessionStatus';
import { QUESTIONS_LIMIT } from '@components/liveQA/constants';
import { SESSION_DETAIL } from '@graphql/operations/sessions/sessionDetail';
import useAppFeatures from '@hooks/useAppFeatures';

type UseSessionInfoProps = {
  sessionId: string;
  webcast?: Webcast;
  fetchPolicy?: WatchQueryFetchPolicy;
  virtualAttendee?: boolean;
};

type UseSessionInfoValue = {
  sessionDetailsResponse: QueryResult<SessionDetailQuery>;
  sessionStatus: string;
  isCancelled: boolean;
  getIsLive: () => boolean;
  engagementDetails: boolean;
};

/**
 *
 * This hook is returning the SESSION_DETAIL query response and
 * some more info for specific sessionId.
 * this hook will use in the PageInfo and useSessionDetails
 *
 */
const useSessionInfo = ({
  sessionId,
  webcast,
  fetchPolicy = 'cache-first',
  virtualAttendee = false
}: UseSessionInfoProps): UseSessionInfoValue => {
  const locale = useLocaleForQueries();
  const { sessionCapacity, engagementDetails } = useAppFeatures();

  const sessionDetailQueryVariables = useMemo(
    () => ({
      id: sessionId,
      qaLimit: QUESTIONS_LIMIT,
      locale,
      virtualAttendee,
      enableSessionCapacity: sessionCapacity || false
    }),
    [sessionId, locale, virtualAttendee, sessionCapacity]
  );

  const localSessionDetailsResponse = useQuery(SESSION_DETAIL, {
    errorPolicy: 'all',
    fetchPolicy,
    variables: sessionDetailQueryVariables,
    skip: !sessionId || typeof locale === 'undefined'
  });

  const isCancelled = localSessionDetailsResponse.data?.sessionDetail?.status === 'Cancelled';

  const sessionDetailsNonPermittedResponse = useMemo(
    () =>
      // Memoize this so error is not continuously recreated
      isCancelled
        ? ({
            error: new Error('Session is cancelled'),
            loading: false,
            data: null,
            refetch: null,
            updateQuery: null
          } as QueryResult<SessionDetailQuery>)
        : null,
    [isCancelled]
  );

  const sessionDetailsResponse = isCancelled ? sessionDetailsNonPermittedResponse : localSessionDetailsResponse;

  const sessionDetails = sessionDetailsResponse.data?.sessionDetail;

  const sessionStatus = useSessionStatus({
    start: sessionDetails?.start,
    end: sessionDetails?.end,
    options: {
      useDetailedAfterStatus: true
    }
  });

  const getIsLive = useIsLive({
    webcast: webcast || sessionDetails?.webcast,
    sessionStatus,
    sessionStart: sessionDetails?.start
  });

  return {
    sessionDetailsResponse,
    sessionStatus,
    isCancelled,
    getIsLive,
    engagementDetails
  };
};

export default useSessionInfo;
